import { useCallback, useEffect, useState } from "preact/hooks";
import { SnackbarShowCommand } from "./Snackbar";
import {
    getProductTypeRevision,
    getSerialNumberInfo,
    Label,
    ManufacturingConfiguration,
    Product,
    ProductTypeRevision,
} from "../api/product-api";
import { findSerialNumberByRegex, generateLabelFields, SearchInStringExec } from "../utils";
import { filterDefaultLabelsForRevision } from "../data/filterDefaultLabelsForRevision";
import { h, Fragment } from "preact";
import { Command } from "../hooks/Command";
import { ScanPackQR } from "./ScanPackQR";
import { LabelSelect } from "./LabelSelect";
import { TemplatePrint } from "./TemplatePrint";
import { resolvePath } from "../api";
import { lunaEMG } from "../data/serialNumberSearchExecs";

export function ProductLabelReprint({
    showSnackbar,
    scanExecs,
    execs,
    configurations,
    additionalAllowedProductTypes,
}: {
    showSnackbar: Command<SnackbarShowCommand>;
    scanExecs: SearchInStringExec[];
    execs: SearchInStringExec[];
    configurations: ManufacturingConfiguration[] | undefined;
    additionalAllowedProductTypes?: string[];
}) {
    const [serialNumber, setSerialNumber] = useState("");
    const [typeInfo, setTypeInfo] = useState(null as { product: Product; revision: ProductTypeRevision } | null);
    const [selectedLabel, setSelectedLabel] = useState(null as null | Label);

    useEffect(() => {
        if (!serialNumber) {
            setTypeInfo(null);
            setSelectedLabel(null);
            return;
        }

        if ((lunaEMG.execs.elementGtinLot8DateRegexPlain, !findSerialNumberByRegex(serialNumber, execs))) {
            setTypeInfo(null);
            setSelectedLabel(null);
            showSnackbar.run("Podany numer seryjny jest w złym formacie", "error", "error", 2000);
            return;
        }

        getSerialNumberInfo([serialNumber]).then(async products => {
            if (products.length === 0) {
                setTypeInfo(null);
                setSelectedLabel(null);
                showSnackbar.run("Podany numer seryjny nie istnieje", "error", "error", 2000);
                return;
            }

            const product = products[0];

            const types = configurations;
            const foundType =
                additionalAllowedProductTypes?.includes(product.productTypeRevision.productType!.name) ||
                types?.find(v => v.productType.name === product.productTypeRevision.productType!.name);

            if (!foundType) {
                setTypeInfo(null);
                setSelectedLabel(null);
                showSnackbar.run("Podany numer seryjny jest nieprawidłowy", "error", "error", 2000);
                return;
            }

            const revision = await getProductTypeRevision(product.productTypeRevision.id);
            showSnackbar.run("Znaleziono numer seryjny", "success", "check", 1000);
            setTypeInfo({ product, revision });
            setSelectedLabel(filterDefaultLabelsForRevision(revision)[0]?.label ?? null);
        });
    }, [serialNumber]);

    const onReadCallback = useCallback((data: string) => {
        const text = JSON.parse(data) as string;
        const serial = findSerialNumberByRegex(text, scanExecs);

        if (!serial) {
            return;
        }

        setSerialNumber(serial);
    }, []);

    return (
        <>
            <div class="paper serial-input">
                <ScanPackQR onRead={onReadCallback} />
                <div>
                    <label>
                        <span style="margin-right: 10px;">
                            Podaj lub zeskanuj numer seryjny naklejki, którą chcesz wydrukować:
                        </span>
                        <input value={serialNumber} onChange={e => setSerialNumber(e.currentTarget.value.trim())} />
                    </label>
                </div>
            </div>
            {typeInfo && (
                <div class="paper" style="padding: 10px; margin-top: 10px; margin-left: 10px; margin-right: 10px">
                    <h1>Naklejki</h1>
                    <LabelSelect revision={typeInfo.revision} selected={selectedLabel} onChange={setSelectedLabel} />
                </div>
            )}
            {typeInfo && selectedLabel ? (
                <div class="paper print-serial">
                    <TemplatePrint
                        fields={{
                            ...generateLabelFields({
                                date: new Date(Date.parse((console.log(typeInfo.product), typeInfo.product.createdAt))),
                                serialNumber,
                                revision: typeInfo.revision,
                            }),
                        }}
                        template={selectedLabel.path}
                        overrideFields={selectedLabel.metadata?.metadata}
                        tapeImgBase={resolvePath("images/tapes/")}
                    />
                </div>
            ) : null}
        </>
    );
}
